import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate } from 'react-router-dom';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { paymentService } from '../services/payment.service';
import { useAppSelector } from '../store';
import { selectUser } from '../store/auth/authSlice';
import { PaymentModal } from '../components/payment/PaymentModal';

export const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showRetryModal, setShowRetryModal] = useState(false);

  useEffect(() => {
    if (!user) return;

    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const checkStatus = async () => {
      try {
        const status = await paymentService.getMySubscription();
        
        if (status.isActive) {
          if (isMounted) {
            setIsSuccess(true);
            setIsPending(false);
            setLoading(false);
          }
        } else if (status.status === 'pending') {
          // Якщо статус pending, продовжуємо чекати, але показуємо відповідний UI
          if (attempts < 10) { // Збільшуємо кількість спроб для pending
             timeoutId = setTimeout(() => {
              if (isMounted) {
                setAttempts(prev => prev + 1);
              }
            }, 3000);
          } else {
             // Якщо довго pending - показуємо екран "В обробці"
             if (isMounted) {
                setIsPending(true);
                setLoading(false);
             }
          }
        } else if (['cancelled', 'failed', 'declined', 'expired', 'payment_failed'].includes(status.status)) {
            // Явна перевірка на неуспішні статуси - одразу показуємо помилку
            if (isMounted) {
                setIsSuccess(false);
                setIsPending(false);
                setLoading(false);
            }
        } else {
          // Якщо підписка не активна і статус незрозумілий (наприклад 'none'), пробуємо ще трохи
          if (attempts < 5) {
            timeoutId = setTimeout(() => {
              if (isMounted) {
                setAttempts(prev => prev + 1);
              }
            }, 3000);
          } else {
            if (isMounted) {
              setIsSuccess(false);
              setIsPending(false);
              setLoading(false);
            }
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        if (isMounted) {
          // При помилці мережі теж можна спробувати ще раз або показати помилку
          if (attempts < 3) {
             timeoutId = setTimeout(() => {
              if (isMounted) {
                setAttempts(prev => prev + 1);
              }
            }, 3000);
          } else {
             setIsSuccess(false);
             setLoading(false);
          }
        }
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, attempts]);

  const handleRetry = () => {
    setShowRetryModal(true);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Перевіряємо статус оплати...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Будь ласка, зачекайте, це може зайняти кілька секунд.
          </Typography>
        </>
      );
    }

    if (isSuccess) {
      return (
        <>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" gutterBottom color="success.main">
            Оплата успішна!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Дякуємо за вашу підписку. Всі функції додатку тепер доступні для вас.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 2 }}
          >
            Перейти на головну
          </Button>
        </>
      );
    }

    if (isPending) {
      return (
        <>
          <HourglassEmptyIcon color="warning" sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" gutterBottom color="warning.main">
            Оплата в обробці
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Ми отримали інформацію про вашу спробу оплати, але банк ще не підтвердив транзакцію.
            Це може зайняти від кількох хвилин до години.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Доступ відкриється автоматично, як тільки ми отримаємо підтвердження.
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 2 }}
          >
            Перейти на головну
          </Button>
        </>
      );
    }

    return (
      <>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h4" gutterBottom color="error.main">
          Оплата не пройшла
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          На жаль, ми не отримали підтвердження оплати або вона була скасована.
        </Typography>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleRetry}
          sx={{ mt: 2 }}
        >
          Спробувати ще раз
        </Button>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          На головну
        </Button>
      </>
    );
  };

  return (
    <Box
      data-i-doc={PageIndices.PAYMENT_SUCCESS}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        {renderContent()}
      </Paper>
      
      {/* Модальне вікно для повторної спроби */}
      <PaymentModal 
        open={showRetryModal} 
        onClose={() => setShowRetryModal(false)} 
        canClose={true} 
      />
    </Box>
  );
};
